/**
 * OverviewTab Component
 * Dashboard overview of widget configuration and quick actions
 */

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { PreviewButton } from './shared/WidgetPreview';
import type { WidgetConfiguration } from '../../types/widget-config';

export interface OverviewTabProps {
  config: WidgetConfiguration;
}

/**
 * OverviewTab provides a high-level summary of widget status
 */
export function OverviewTab({ config }: OverviewTabProps) {
  const lastUpdated = new Date(config.updatedAt);
  const activeServices = config.services.filter(s => s.available).length;
  const totalServices = config.services.length;

  return (
    <div className="space-y-6">
      {/* Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Widget Status */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Widget Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <div className="h-3 w-3 rounded-full bg-green-500" />
              <span className="text-sm font-medium text-green-700">Active</span>
            </div>
            <p className="mt-2 text-sm text-gray-600">
              Your widget is live and accepting bookings
            </p>
          </CardContent>
        </Card>

        {/* Services */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Services Offered</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-gray-900">
              {activeServices}
            </div>
            <p className="text-sm text-gray-600">
              {activeServices} of {totalServices} container sizes available
            </p>
          </CardContent>
        </Card>

        {/* Last Updated */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Last Updated</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-lg font-medium text-gray-900">
              {lastUpdated.toLocaleDateString()}
            </div>
            <p className="text-sm text-gray-600">
              {lastUpdated.toLocaleTimeString()}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Configuration Summary */}
      <Card>
        <CardHeader>
          <CardTitle>Configuration Summary</CardTitle>
          <CardDescription>
            Current widget settings at a glance
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Branding */}
          <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
            <div className="flex-shrink-0">
              {config.branding.logoUrl ? (
                <img
                  src={config.branding.logoUrl}
                  alt="Company logo"
                  className="h-12 w-auto rounded"
                />
              ) : (
                <div className="h-12 w-12 rounded bg-gray-300 flex items-center justify-center">
                  <svg
                    className="h-6 w-6 text-gray-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                </div>
              )}
            </div>
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-gray-900">
                {config.branding.companyName}
              </h4>
              <div className="mt-2 flex items-center gap-2">
                <span className="text-xs text-gray-600">Primary Color:</span>
                <div
                  className="h-6 w-12 rounded border border-gray-300"
                  style={{ backgroundColor: config.branding.primaryColor }}
                  title={config.branding.primaryColor}
                />
                <span className="text-xs font-mono text-gray-700">
                  {config.branding.primaryColor}
                </span>
              </div>
              {config.branding.fontFamily && (
                <p className="mt-1 text-xs text-gray-600">
                  Font: {config.branding.fontFamily}
                </p>
              )}
            </div>
          </div>

          {/* Pricing */}
          <div className="p-4 bg-gray-50 rounded-lg">
            <h4 className="text-sm font-semibold text-gray-900 mb-2">
              Pricing Configuration
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {Object.entries(config.pricing.basePricing).map(([size, price]) => (
                <div key={size} className="text-center">
                  <div className="text-xs text-gray-600">{size}</div>
                  <div className="text-lg font-semibold text-gray-900">
                    ${price}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-3 pt-3 border-t border-gray-200 text-sm text-gray-600">
              Rental Period: {config.pricing.rentalPeriodDays} days
              {config.pricing.deliveryFee && ` • Delivery Fee: $${config.pricing.deliveryFee}`}
              {config.pricing.taxRate && ` • Tax Rate: ${(config.pricing.taxRate * 100).toFixed(2)}%`}
            </div>
          </div>

          {/* Contact Info */}
          <div className="p-4 bg-gray-50 rounded-lg">
            <h4 className="text-sm font-semibold text-gray-900 mb-2">
              Contact Information
            </h4>
            <div className="space-y-1 text-sm text-gray-600">
              <p>Email: {config.businessInfo.contactEmail}</p>
              <p>Phone: {config.businessInfo.contactPhone}</p>
              {config.businessInfo.website && (
                <p>
                  Website:{' '}
                  <a
                    href={config.businessInfo.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-600 hover:underline"
                  >
                    {config.businessInfo.website}
                  </a>
                </p>
              )}
              {config.businessInfo.businessHours && (
                <p>Hours: {config.businessInfo.businessHours}</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
          <CardDescription>
            Common tasks and helpful links
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            <PreviewButton
              slug={config.slug}
              className="flex items-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-md hover:bg-primary-700 transition-colors"
            />
            <Button
              variant="outline"
              onClick={() => window.open(`/widget/${config.slug}`, '_blank')}
            >
              <svg
                className="w-5 h-5 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                />
              </svg>
              View Live Widget
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
